import React, { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Heading from '@vfuk/core-heading';
import { Link } from 'gatsby';
import { ReactComponent as ChevronRight } from '../../../../images/icon/chevron-right.svg';
import Tags from '../../../Tags/Tags';
import { ArticleClickEventData, ArticleProps, CustomWindow } from './types/Article.types';
import replaceSlug from '../../../../helpers/replaceSlugs';
import reduceLookUpTable from '../../../../helpers/reduceLookUpTable';
import {
  ArticleWrapper,
  imageBlock,
  focalPointImageStyle,
  TextBlock,
  HeaderText,
  ChevronNewLine,
  TagsBarWrapper,
  bgWhite,
  bgAbbey,
} from './styles/Article.styles';
import { DirectionContext } from '../../../../contexts/DirectionContext';

const queryLookupData = graphql`
  {
    contentfulSlugCharacterLookup {
      name
      exceptions
      lookup {
        lookup
      }
    }
  }
`;

declare const window: CustomWindow;

const fireArticleClickEvent = (trackingData: ArticleClickEventData) => {
  if (window.onArticleClickEvent) {
    try {
      // fire the utag link event
      window.onArticleClickEvent(trackingData);
    } catch (e) {
      console.log(
        'onArticleClickEvent failed - please check the Article Clicks SEO script'
      );
    }
  }
};

export const Article = ({
  data,
  indexSlug,
  backgroundColor,
  seoTitle,
  appliedFilters
}: ArticleProps) => {
  const {
    pageArticleName: name,
    mainImage,
    articleThumbnailImage,
    summary,
    slug,
    createdAt,
    categoryTags,
    topicTags,
  } = data;

  const linkCSS = backgroundColor === 'white' ? bgWhite : bgAbbey;

  // Replace characters within the slug, using the Slug character look up from Contentful
  const { contentfulSlugCharacterLookup } = useStaticQuery(queryLookupData);
  const exceptions = contentfulSlugCharacterLookup?.exceptions;
  const lookup = contentfulSlugCharacterLookup?.lookup?.lookup;

  const slugCharLookUpTable = reduceLookUpTable(lookup);
  const replacedSlug = replaceSlug(slug, slugCharLookUpTable, exceptions);

  let imageComponent;

  if (articleThumbnailImage && articleThumbnailImage !== null) {
    const {
      image: {
        description,
        gatsbyImageData,
        file: {
          details: {
            image: { height: imageHeight, width: imageWidth },
          },
        },
      },
      focalPoint: {
        focalPoint: { x: focalPointX, y: focalPointY },
      },
    } = articleThumbnailImage;

    const xCoordinate = (focalPointX / imageWidth) * 100;
    const yCoordinate = (focalPointY / imageHeight) * 100;

    imageComponent = (
      <GatsbyImage
        image={getImage(gatsbyImageData) as IGatsbyImageData}
        imgStyle={{
          objectFit: 'cover',
          objectPosition: `${xCoordinate}% ${yCoordinate}%`,
        }}
        css={focalPointImageStyle}
        alt={description}
      />
    );
  } else if (mainImage && mainImage !== null) {
    imageComponent = (
      <GatsbyImage
        image={getImage(mainImage) as IGatsbyImageData}
        css={imageBlock}
        alt={mainImage.description}
      />
    );
  }

  const splitReplacedSlug = replacedSlug.split('/');
  const updatedSlug = splitReplacedSlug.length === 2 ? splitReplacedSlug[1] : splitReplacedSlug[0];
  const direction = useContext(DirectionContext);

  return (
    <ArticleWrapper>
      <Link
        css={linkCSS}
        to={`/${indexSlug}/${updatedSlug}`}
        title={name}
        data-testid={`article-${updatedSlug}`}
        data-analytics-element={name}
        data-analytics-category="article"
        data-analytics-context={seoTitle}
        onClick={() => fireArticleClickEvent({ appliedFilters, contentName: name })}
      >
        {imageComponent}
        <TextBlock direction={direction}>
          <HeaderText direction={direction}>
            <Heading level={4} weight={2}>
              {name}
              <ChevronNewLine>
                &#65279;
                <ChevronRight />
              </ChevronNewLine>
            </Heading>
          </HeaderText>
          <TagsBarWrapper>
            <p>{createdAt}</p>
            {categoryTags && <Tags tags={categoryTags} />}
            {topicTags && <Tags tags={topicTags} />}
          </TagsBarWrapper>
          <p>{summary}</p>
        </TextBlock>
      </Link>
    </ArticleWrapper>
  );
};
